/**
 * UserManagementTab - Admin Dashboard for User Lifecycle
 * ======================================================
 * SESSION 109: SaaS Auth
 * 
 * Features:
 * - Tabbed view: Pending | Active | Expired
 * - User metrics (Total, Active, Expired count)
 * - Search functionality
 * - Grant/Extend subscription action
 * - Revoke subscription action
 * - Delete user action
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Users, 
  UserCheck, 
  UserX, 
  Clock, 
  Calendar,
  MoreVertical,
  RefreshCw,
  Trash2,
  Shield,
  CheckCircle2,
  XCircle,
  Mail,
  Link2,
  Copy,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Preset subscription durations
const DURATION_PRESETS = [
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: '6 Months', days: 180 },
  { label: '1 Year', days: 365 },
];

export const UserManagementTab = () => {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState({ pending: [], active: [], expired: [] });
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, expired: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // user_id being processed
  
  // Modal states
  const [grantModal, setGrantModal] = useState({ open: false, user: null, customDate: '' });
  const [revokeModal, setRevokeModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/api/admin/users?search=${encodeURIComponent(searchQuery)}`);
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users);
      setStats(data.stats);
    } catch (err) {
      toast.error('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, searchQuery]);

  // Fetch invites
  const fetchInvites = useCallback(async () => {
    try {
      setInvitesLoading(true);
      const res = await authFetch(`${API}/api/admin/invites`);
      if (!res.ok) throw new Error('Failed to fetch invites');
      const data = await res.json();
      setInvites(data.invites || []);
    } catch (err) {
      console.error('Failed to load invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchUsers();
    fetchInvites();
  }, [fetchUsers, fetchInvites]);

  // Generate invite
  const handleGenerateInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setInviteLoading(true);
      setInviteResult(null);
      const res = await authFetch(`${API}/api/admin/invites/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to generate invite');
        return;
      }
      setInviteResult(data);
      setInviteEmail('');
      toast.success(`Invite generated for ${data.email}`);
      fetchInvites();
    } catch (err) {
      toast.error('Failed to generate invite: ' + err.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Grant subscription
  const handleGrant = async (userId, days, customDate = null) => {
    try {
      setActionLoading(userId);
      let endDate;
      
      if (customDate) {
        // Use custom date directly
        endDate = new Date(customDate);
      } else {
        // Calculate from days
        endDate = new Date();
        endDate.setDate(endDate.getDate() + days);
      }
      
      // Set expiry to 4:00 PM IST (end of market hours)
      // IST is UTC+5:30, so 4 PM IST = 10:30 AM UTC
      endDate.setUTCHours(10, 30, 0, 0);
      
      const res = await authFetch(`${API}/api/admin/users/${userId}/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_end_date: endDate.toISOString() })
      });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Failed to grant subscription (${res.status})`);
      }
      
      toast.success(customDate ? `Subscription granted until ${endDate.toLocaleDateString()} 4:00 PM IST` : `Subscription granted for ${days} days (until 4:00 PM IST)`);
      setGrantModal({ open: false, user: null, customDate: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Revoke subscription
  const handleRevoke = async (userId) => {
    try {
      setActionLoading(userId);
      const res = await authFetch(`${API}/api/admin/users/${userId}/revoke`, {
        method: 'POST',
      });
      
      if (!res.ok) throw new Error('Failed to revoke subscription');
      
      toast.success('Subscription revoked and user logged out');
      setRevokeModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    try {
      setActionLoading(userId);
      const res = await authFetch(`${API}/api/admin/users/${userId}`, {
        method: 'DELETE',
      });
      
      if (!res.ok) throw new Error('Failed to delete user');
      
      toast.success('User deleted successfully');
      setDeleteModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Get current tab's users
  const currentUsers = users[activeTab] || [];

  // Filter by search
  const filteredUsers = currentUsers.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="user-management-tab">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-white">User Management</h2>
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          data-testid="refresh-users-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50">
          <div className="text-2xl font-bold text-white">{stats.total}</div>
          <div className="text-xs text-gray-500">Total Users</div>
        </div>
        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
          <div className="text-2xl font-bold text-green-400">{stats.active}</div>
          <div className="text-xs text-green-500/70">Active</div>
        </div>
        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
          <div className="text-2xl font-bold text-blue-400">{stats.pending}</div>
          <div className="text-xs text-blue-500/70">Pending</div>
        </div>
        <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
          <div className="text-2xl font-bold text-amber-400">{stats.expired}</div>
          <div className="text-xs text-amber-500/70">Expired</div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center justify-between gap-4">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-800/50 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'pending' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-pending"
          >
            <Clock className="w-4 h-4 inline mr-1" />
            Pending ({users.pending?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'active' 
                ? 'bg-green-500/20 text-green-400' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-active"
          >
            <UserCheck className="w-4 h-4 inline mr-1" />
            Active ({users.active?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('expired')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'expired' 
                ? 'bg-amber-500/20 text-amber-400' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-expired"
          >
            <UserX className="w-4 h-4 inline mr-1" />
            Expired ({users.expired?.length || 0})
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 w-64"
            data-testid="user-search-input"
          />
        </div>
      </div>

      {/* User Table */}
      <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No users found in this category</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Valid till</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Plan / Expiry</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Telegram</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-800/30 transition-colors" data-testid={`user-row-${user.id}`}>
                  {/* User Info */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                          <span className="text-gray-400 text-xs font-medium">
                            {(user.name || user.email || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-white">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  
                  {/* Role */}
                  <td className="px-4 py-3">
                    {user.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium">
                        <Shield className="w-3 h-3" />
                        Admin
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">User</span>
                    )}
                  </td>
                  
                  {/* Status */}
                  <td className="px-4 py-3">
                    {(() => {
                      // Check if subscription is expired
                      const isExpired = user.subscription_end_date && new Date(user.subscription_end_date) < new Date();
                      
                      if (user.role === 'admin') {
                        // Admin is always active
                        return (
                          <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Active
                          </span>
                        );
                      } else if (isExpired) {
                        // Subscription expired
                        return (
                          <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                            <XCircle className="w-3 h-3" />
                            Expired
                          </span>
                        );
                      } else if (user.is_active) {
                        return (
                          <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Active
                          </span>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </span>
                        );
                      }
                    })()}
                  </td>
                  
                  {/* Subscription */}
                  <td className="px-4 py-3">
                    {user.role === 'admin' ? (
                      <span className="text-xs text-purple-400">Unlimited</span>
                    ) : user.subscription_end_date ? (
                      <div>
                        <p className="text-xs text-gray-300">
                          {new Date(user.subscription_end_date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            timeZone: 'Asia/Kolkata'
                          })}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {(() => {
                            const endDate = new Date(user.subscription_end_date);
                            const now = new Date();
                            const diffMs = endDate - now;
                            
                            if (diffMs <= 0) return 'Expired';
                            
                            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                            const diffDays = Math.floor(diffHours / 24);
                            const remainingHours = diffHours % 24;
                            
                            if (diffDays > 0) {
                              return `${diffDays}d ${remainingHours}h left`;
                            } else if (diffHours > 0) {
                              const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                              return `${diffHours}h ${diffMinutes}m left`;
                            } else {
                              const diffMinutes = Math.floor(diffMs / (1000 * 60));
                              return `${diffMinutes}m left`;
                            }
                          })()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">—</span>
                    )}
                  </td>
                  
                  {/* Last Login */}
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-400">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Asia/Kolkata'
                          }) + ' IST'
                        : '—'
                      }
                    </span>
                  </td>
                  
                  {/* Plan + Expiry */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user.role === 'admin' ? (
                        <span className="px-2 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded border border-green-500/30">PAID</span>
                      ) : (
                        <button
                          onClick={async () => {
                            const newPlan = (user.subscription_plan === 'paid') ? 'free' : 'paid';
                            try {
                              const res = await authFetch(`${API}/api/admin/users/${user.id}/subscription-plan`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ subscription_plan: newPlan }),
                              });
                              if (res.ok) {
                                toast.success(`${user.email} set to ${newPlan.toUpperCase()}`);
                                fetchUsers();
                              }
                            } catch {}
                          }}
                          className={`px-2 py-0.5 text-[10px] rounded border transition-colors cursor-pointer ${
                            user.subscription_plan === 'paid'
                              ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30'
                              : 'bg-gray-500/20 text-gray-400 border-gray-500/30 hover:bg-green-500/10 hover:text-green-400 hover:border-green-500/30'
                          }`}
                          data-testid={`plan-toggle-${user.id}`}
                          title={user.subscription_plan === 'paid' ? 'Click to set FREE' : 'Click to set PAID'}
                        >
                          {user.subscription_plan === 'paid' ? 'PAID' : 'FREE'}
                        </button>
                      )}
                      {user.role !== 'admin' && user.subscription_plan === 'paid' && (
                        <input
                          type="date"
                          value={user.subscription_end_date ? user.subscription_end_date.split('T')[0] : ''}
                          onChange={async (e) => {
                            const expiryDate = e.target.value;
                            try {
                              const res = await authFetch(`${API}/api/admin/users/${user.id}/subscription-plan`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ subscription_plan: 'paid', subscription_expires_at: expiryDate }),
                              });
                              if (res.ok) {
                                toast.success(`Expiry set to ${expiryDate}`);
                                fetchUsers();
                              }
                            } catch {}
                          }}
                          className="bg-gray-800/50 border border-gray-700/50 rounded px-1.5 py-0.5 text-[10px] text-gray-300 focus:outline-none focus:border-cyan-500/50"
                          data-testid={`expiry-date-${user.id}`}
                          title="Subscription expiry date"
                        />
                      )}
                      {user.subscription_end_date && user.subscription_plan === 'free' && (() => {
                        // Session 147j+16bv — Previously this badge always
                        // said "Expired" for any free-plan user with any
                        // subscription_end_date, even when the date was in
                        // the future. Admins panicked thinking the user
                        // shouldn't be logging in (when they actually still
                        // had valid access). Now we compare against `now`
                        // and show "Renews" for future, "Expired" for past.
                        const endDate = new Date(user.subscription_end_date);
                        const isPastDue = endDate < new Date();
                        const isoDate = user.subscription_end_date.split('T')[0];
                        return (
                          <span
                            className={`text-[9px] ${isPastDue ? 'text-red-400' : 'text-muted-foreground/70'}`}
                            title={isPastDue
                              ? 'Subscription expired — user is blocked from new logins.'
                              : 'Free plan ends on this date. User can log in until then.'}
                            data-testid={`plan-expiry-${user.id}`}
                          >
                            {isPastDue ? 'Expired' : 'Renews'} {isoDate}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  
                  {/* Telegram Status */}
                  <td className="px-4 py-3">
                    {user.telegram_linked ? (
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${user.telegram_channel_member ? 'bg-green-400' : 'bg-amber-400'}`} />
                        <span className="text-[10px] text-gray-400" title={user.telegram_username || user.telegram_chat_id}>
                          {user.telegram_channel_member ? 'In Channel' : 'Linked'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-600">Not linked</span>
                    )}
                  </td>
                  
                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    {user.role !== 'admin' && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setGrantModal({ open: true, user, customDate: '' })}
                          disabled={actionLoading === user.id}
                          className="px-2 py-1 text-xs font-medium text-green-400 hover:bg-green-500/10 rounded transition-colors"
                          data-testid={`grant-btn-${user.id}`}
                        >
                          {activeTab === 'active' ? 'Extend' : 'Grant'}
                        </button>
                        {user.is_active && (
                          <button
                            onClick={() => setRevokeModal({ open: true, user })}
                            disabled={actionLoading === user.id}
                            className="px-2 py-1 text-xs font-medium text-amber-400 hover:bg-amber-500/10 rounded transition-colors"
                            data-testid={`revoke-btn-${user.id}`}
                          >
                            Revoke
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModal({ open: true, user })}
                          disabled={actionLoading === user.id}
                          className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          data-testid={`delete-btn-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ============================================ */}
      {/* INVITE MANAGEMENT SECTION                    */}
      {/* ============================================ */}
      <div className="mt-6 space-y-4" data-testid="invite-management-section">
        <div className="flex items-center gap-2 mb-3">
          <Send className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-white">Invite Management</h2>
        </div>

        {/* Generate Invite Form */}
        <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 p-4">
          <p className="text-xs text-gray-500 mb-3">Generate a one-time invite link for a specific email address</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerateInvite()}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                data-testid="invite-email-input"
              />
            </div>
            <button
              onClick={handleGenerateInvite}
              disabled={inviteLoading || !inviteEmail}
              className="px-5 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-sm text-cyan-400 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="generate-invite-btn"
            >
              <Link2 className="w-4 h-4" />
              {inviteLoading ? 'Generating...' : 'Generate Invite'}
            </button>
          </div>

          {/* Generated invite result */}
          {inviteResult && (
            <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="invite-result">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-green-400 mb-1">Invite link for <span className="font-medium">{inviteResult.email}</span></p>
                  <p className="text-sm text-white font-mono truncate">{inviteResult.invite_url}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(inviteResult.invite_url)}
                  className="flex-shrink-0 p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                  data-testid="copy-invite-url-btn"
                  title="Copy invite link"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Invite List */}
        <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700/50 flex items-center justify-between">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Recent Invites</p>
            <button
              onClick={fetchInvites}
              disabled={invitesLoading}
              className="text-gray-500 hover:text-white transition-colors"
              data-testid="refresh-invites-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${invitesLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {invites.length === 0 ? (
            <div className="text-center py-6 text-gray-600 text-sm">No invites generated yet</div>
          ) : (
            <div className="divide-y divide-gray-700/30 max-h-60 overflow-y-auto">
              {invites.map((inv, i) => (
                <div key={inv.code || i} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-800/30" data-testid={`invite-row-${inv.code}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${inv.used ? 'bg-gray-500' : 'bg-green-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{inv.email || 'No email'}</p>
                      <p className="text-[10px] text-gray-600 font-mono">{inv.code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {inv.used ? (
                      <span className="text-[10px] text-gray-500 bg-gray-800 px-2 py-0.5 rounded">
                        Used{inv.used_by ? ` by ${inv.used_by}` : ''}
                      </span>
                    ) : (
                      <>
                        <span className="text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Pending</span>
                        <button
                          onClick={() => {
                            const redirect_uri = process.env.REACT_APP_BACKEND_URL || '';
                            const url = `${redirect_uri.replace('/api', '')}/invite/${inv.code}`;
                            // Use the same frontend base as inviteResult or derive from window
                            const frontendBase = window.location.origin;
                            copyToClipboard(`${frontendBase}/invite/${inv.code}`);
                          }}
                          className="p-1 text-gray-500 hover:text-cyan-400 transition-colors"
                          title="Copy invite link"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grant Modal */}
      {grantModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setGrantModal({ open: false, user: null, customDate: '' })}>
          <div 
            className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4" 
            onClick={e => e.stopPropagation()}
            data-testid="grant-modal"
          >
            <h3 className="text-lg font-semibold text-white mb-4">
              {grantModal.user?.subscription_end_date ? 'Extend' : 'Grant'} Subscription
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Select duration for <span className="text-white">{grantModal.user?.email}</span>
            </p>
            
            {/* Preset duration buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {DURATION_PRESETS.map(preset => (
                <button
                  key={preset.days}
                  onClick={() => handleGrant(grantModal.user?.id, preset.days)}
                  disabled={actionLoading === grantModal.user?.id}
                  className="px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500/50 rounded-lg text-sm text-white font-medium transition-all disabled:opacity-50"
                  data-testid={`grant-${preset.days}-days`}
                >
                  {actionLoading === grantModal.user?.id ? '...' : preset.label}
                </button>
              ))}
            </div>
            
            {/* Custom date picker */}
            <div className="border-t border-gray-700 pt-4 mt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Or select custom date</p>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={grantModal.customDate || ''}
                  onChange={(e) => setGrantModal(prev => ({ ...prev, customDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/50"
                  data-testid="custom-date-input"
                />
                <button
                  onClick={() => grantModal.customDate && handleGrant(grantModal.user?.id, null, grantModal.customDate)}
                  disabled={!grantModal.customDate || actionLoading === grantModal.user?.id}
                  className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-sm text-cyan-400 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="apply-custom-date"
                >
                  Apply
                </button>
              </div>
            </div>
            
            <button
              onClick={() => setGrantModal({ open: false, user: null, customDate: '' })}
              className="w-full px-4 py-2 mt-4 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {revokeModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRevokeModal({ open: false, user: null })}>
          <div 
            className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4" 
            onClick={e => e.stopPropagation()}
            data-testid="revoke-modal"
          >
            <h3 className="text-lg font-semibold text-white mb-2">Revoke Subscription</h3>
            <p className="text-sm text-gray-400 mb-4">
              This will immediately revoke access for <span className="text-white">{revokeModal.user?.email}</span> and log them out.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setRevokeModal({ open: false, user: null })}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevoke(revokeModal.user?.id)}
                disabled={actionLoading === revokeModal.user?.id}
                className="flex-1 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-lg text-sm text-amber-400 font-medium transition-colors"
                data-testid="confirm-revoke-btn"
              >
                {actionLoading === revokeModal.user?.id ? 'Revoking...' : 'Revoke Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setDeleteModal({ open: false, user: null })}>
          <div 
            className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4" 
            onClick={e => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <h3 className="text-lg font-semibold text-white mb-2">Delete User</h3>
            <p className="text-sm text-gray-400 mb-4">
              Are you sure you want to permanently delete <span className="text-white">{deleteModal.user?.email}</span>? This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.user?.id)}
                disabled={actionLoading === deleteModal.user?.id}
                className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-sm text-red-400 font-medium transition-colors"
                data-testid="confirm-delete-btn"
              >
                {actionLoading === deleteModal.user?.id ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
